import React, { useState } from "react";
import PropTypes from "prop-types";

/**
 * Sort medications alphabetically by Name (case-insensitive).
 * Returns a new array (does not mutate props).
 * @param {Array} medications
 * @returns {Array}
 */
const sortMedsByName = (medications) => {
  if (!Array.isArray(medications)) return [];

  return medications
    .slice(0)
    .sort((a, b) => {
      const aName = (a && a.Name ? String(a.Name) : "").toLowerCase();
      const bName = (b && b.Name ? String(b.Name) : "").toLowerCase();

      if (aName < bName) return -1;
      if (aName > bName) return 1;
      return 0;
    });
};

/**
 * DiseaseFundMeds
 * Safari/iOS-safe expand/collapse:
 * - No ExpandToggle dependency
 * - No optional chaining / nullish coalescing (Bublé-safe)
 * - Collapsed is default via CSS (max-height: 450px)
 * - Expanded toggles `.is-expanded` class on the <ol>
 */
const DiseaseFundMeds = (props) => {
  const medications = props.medications;
  const jumpLinkAnchors = props.jumpLinkAnchors;
  const medicationsHeading = props.medicationsHeading;
  const medicationsContent = props.medicationsContent;

  const [expanded, setExpanded] = useState(false);

  // Single source of truth for anchors
  const anchors = Array.isArray(jumpLinkAnchors) ? jumpLinkAnchors : [];

  // Choose which anchor index should be used for the Meds section.
  // (Keeping your prior intent: "use the 2nd jump link")
  const medAnchorId =
    anchors && anchors[1] && anchors[1].id ? anchors[1].id : undefined;

  const sortedMeds = sortMedsByName(medications);

  const onToggle = () => {
    setExpanded(!expanded);
  };

  // If there are no meds, render nothing (or you can render the section w/ empty state).
  // Keeping it simple and safe.
  if (!sortedMeds || sortedMeds.length === 0) {
    return null;
  }

  return (
    <div className="l-padding bg-white u-relative">
      <div className="l-wrap covered-medications" id={medAnchorId}>
        {medicationsHeading ? (
          <h2 className="f-h2 u-mb4">{medicationsHeading}</h2>
        ) : null}

        <div className="Glossary Glossary-meds Glossary--compact">
          <div className={"Glossary-clip " + (expanded ? "is-expanded" : "is-collapsed")}>
            <ol id="diseaseFundMedications" className="Glossary-items">
              {sortedMeds.map((medication, i) => {
                const medName = medication && medication.Name ? String(medication.Name) : "";
                const key = (medName || "med") + "-" + i;

                return (
                  <li key={key} className="Glossary-item">
                    <span className="Glossary-label">{medName}</span>
                  </li>
                );
              })}
            </ol>
          </div>

          <button
            className="Glossary-expand"
            type="button"
            onClick={onToggle}
            aria-controls="diseaseFundMedications"
            aria-expanded={expanded ? "true" : "false"}
          >
            {expanded ? "Collapse —" : "Show All +"}
          </button>
        </div>

        {medicationsContent ? (
          <div
            className="u-richtext meds-content"
            dangerouslySetInnerHTML={{ __html: medicationsContent }}
          />
        ) : null}
      </div>
    </div>
  );
};

DiseaseFundMeds.propTypes = {
  medications: PropTypes.array.isRequired,
  groupId: PropTypes.string.isRequired,
  jumpLinkAnchors: PropTypes.array, // [{label, id, href}]
  medicationsHeading: PropTypes.string,
  medicationsContent: PropTypes.string,
};

DiseaseFundMeds.defaultProps = {
  jumpLinkAnchors: [],
  medicationsHeading: "",
  medicationsContent: "",
};

export default DiseaseFundMeds;